import passengerTypeStore, { PassengerTicketSource } from 'Models/PassengerTypeStore';
import { PassengerTypeKey } from 'Models/Enums';
// Type-only: PassengerDetail.tsx imports the assignment helpers back out of here, and a value
// import would close a module cycle for no reason. Nothing here constructs a PassengerDetail.
import type { PassengerDetail } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';

/**
 * Which passenger on a vehicle booking is the driver.
 *
 * The flag lives on individual `PassengerDetail` rows and exactly one of them across every
 * passenger type should carry it. `PassengerTypeEntity.CanBeDriver` decides which types are
 * eligible, so none of this may assume the driver is an adult.
 *
 * <b>Every search here runs over `allPassengerTypes`, not `passengerTypes`.</b> The enabled list is
 * for rendering. A booking made before the operator disabled a passenger type still carries
 * passengers of it, and searching the enabled list reported those bookings as driverless — which
 * made the repair paths below flag a *second* driver on an enabled type. See the comment on
 * `PassengerTypeStore.allPassengerTypes`.
 *
 * Written against the ticket arrays rather than against the wizard's data class, because the
 * booking wizard and the check-in form hold the same eight arrays under the same names
 * (`getWizardDataProperty` and `getBookingDtoProperty` agree) and both flows have to behave the
 * same way.
 *
 * <b>Every function here reassigns the arrays it touches rather than only mutating them in place.</b>
 * Check-in's form state is a MobX observable and those arrays are `observable` — but MobX's deep
 * enhancer only converts plain objects, arrays, Maps and Sets, and `PassengerDetail` is a class
 * instance, so it is stored as-is and `ticket.driver = true` notifies nobody. The observer
 * components would keep rendering the previous value: the click worked, the state changed, and the
 * screen did not move. Reassigning the array property is what makes the change visible. Callers in
 * check-in still wrap these in `runInAction`.
 */

function propertyFor(key: PassengerTypeKey) {
	return passengerTypeStore.getWizardDataProperty(key);
}

function ticketsFor(source: PassengerTicketSource, key: PassengerTypeKey): PassengerDetail[] | undefined {
	return source[propertyFor(key)];
}

/**
 * Writes the array back onto the source under its own name, so an observable property notifies.
 * A no-op in shape — same elements, same order — and deliberately unconditional, because the
 * caller has just mutated something inside it.
 */
function touch(source: PassengerTicketSource, key: PassengerTypeKey, tickets: PassengerDetail[]): void {
	source[propertyFor(key)] = [...tickets];
}

/** The passenger marked as the driver, whatever their type, or undefined when there is none. */
export function findDriverTicket(source: PassengerTicketSource | null | undefined): PassengerDetail | undefined {
	if (!source) {
		return undefined;
	}
	for (const passengerType of passengerTypeStore.allPassengerTypes) {
		const driver = ticketsFor(source, passengerType.passengerTypeKey)?.find(
			ticket => ticket?.driver === true,
		);
		if (driver) {
			return driver;
		}
	}
	return undefined;
}

/** True when some passenger, of any type, is currently marked as the driver. */
export function hasAnyDriver(source: PassengerTicketSource | null | undefined): boolean {
	return findDriverTicket(source) !== undefined;
}

/**
 * Clears the flag everywhere. Used when a booking stops being a vehicle booking, and as the first
 * half of moving the driver.
 */
export function clearDriver(source: PassengerTicketSource | null | undefined): void {
	if (!source) {
		return;
	}
	for (const passengerType of passengerTypeStore.allPassengerTypes) {
		const key = passengerType.passengerTypeKey;
		const tickets = ticketsFor(source, key);
		// Only the arrays that actually held the flag need reassigning, so a counter for a type that
		// had nothing to clear is not re-rendered for no reason.
		if (tickets?.some(ticket => ticket?.driver === true)) {
			for (const ticket of tickets) {
				ticket.driver = false;
			}
			touch(source, key, tickets);
		}
	}
}

/**
 * Makes the first passenger of `key` the driver, clearing the flag everywhere else first so the
 * booking can never end up with two. No-op when that type has no passengers.
 *
 * Returns whether anything changed.
 */
export function setDriverPassengerType(
	source: PassengerTicketSource | null | undefined,
	key: PassengerTypeKey,
): boolean {
	if (!source) {
		return false;
	}
	const tickets = ticketsFor(source, key);
	if (!tickets || tickets.length === 0) {
		return false;
	}

	clearDriver(source);
	tickets[0].driver = true;
	touch(source, key, tickets);
	return true;
}

/**
 * Assigns a driver when the booking has none, picking the first passenger of the first eligible
 * type in the operator's configured order. Returns whether anything changed, so callers only write
 * state back when it did.
 */
export function ensureDriverAssigned(source: PassengerTicketSource | null | undefined): boolean {
	if (!source || hasAnyDriver(source)) {
		return false;
	}

	for (const passengerType of passengerTypeStore.allPassengerTypes) {
		const key = passengerType.passengerTypeKey;
		if (!passengerTypeStore.getCanBeDriver(key)) {
			continue;
		}
		const tickets = ticketsFor(source, key);
		if (tickets && tickets.length > 0) {
			tickets[0].driver = true;
			touch(source, key, tickets);
			return true;
		}
	}

	return false;
}

/**
 * Of the passenger types currently holding the flag, the one that keeps it.
 *
 * <b>The adult wins; failing that, the lowest `PassengerTypeEntity.Order`.</b>
 *
 * <p>This resolves a *conflict* between two flags that already exist, which is a different question
 * from {@link ensureDriverAssigned}'s "nobody is driving, pick someone" — that one stays on the
 * operator's configured order, because an operator who puts a type first is expressing a default.
 * Here there is no default to express: the booking was saved with two drivers and one of them is an
 * artefact of the bug that wrote it. Adult is the better guess at which one the customer chose,
 * and `Order` is operator-configurable so it cannot carry that meaning on its own — the old comment
 * here claimed "an adult wins over a senior", which was only ever true when `Order` happened to put
 * A first.</p>
 */
function winningDriverKey(source: PassengerTicketSource): PassengerTypeKey | undefined {
	const flagged = passengerTypeStore.allPassengerTypes
		.map(passengerType => passengerType.passengerTypeKey)
		.filter(key => ticketsFor(source, key)?.some(ticket => ticket?.driver === true));

	// allPassengerTypes is sorted by Order, so flagged[0] is already the lowest-Order fallback.
	return flagged.find(key => key === 'A') ?? flagged[0];
}

/**
 * Reduces a booking that already carries more than one driver back to exactly one — the first
 * flagged passenger of {@link winningDriverKey}'s type.
 *
 * Bookings exist in the data with the flag set on passengers of two different types. Nothing in the
 * UI can repair them: the counter's "Set as driver" control is only rendered for a type that does
 * *not* hold the flag, and its handler returns early when the type already does, so neither of the
 * two drivers can be cleared and the second one survives every subsequent edit. Normalising on the
 * way into an edit leaves exactly one flagged, which is a state the toggle can move.
 *
 * <b>Unlike the rest of this file, this does not mutate the tickets it clears</b> — it substitutes a
 * copy with `driver: false`. The arrays handed to an edit form are the same array instances the
 * booking summary holds, and clearing in place would silently change what the booking detail view
 * and the check-in scanner result render for a booking nobody edited.
 *
 * Returns whether anything changed.
 */
export function normaliseSingleDriver(source: PassengerTicketSource | null | undefined): boolean {
	if (!source) {
		return false;
	}

	const winner = winningDriverKey(source);
	if (winner === undefined) {
		return false;
	}

	let driverKept = false;
	let changed = false;

	for (const passengerType of passengerTypeStore.allPassengerTypes) {
		const key = passengerType.passengerTypeKey;
		const tickets = ticketsFor(source, key);
		if (!tickets) {
			continue;
		}

		let typeChanged = false;
		const normalised = tickets.map(ticket => {
			if (ticket?.driver !== true) {
				return ticket;
			}
			if (key === winner && !driverKept) {
				driverKept = true;
				return ticket;
			}
			typeChanged = true;
			return { ...ticket, driver: false };
		});

		if (typeChanged) {
			touch(source, key, normalised);
			changed = true;
		}
	}

	return changed;
}

/** The driver details the booking holds — what gets copied onto whoever is driving. */
export interface DriverDetails {
	firstName?: string | null;
	lastName?: string | null;
	phone?: string | null;
}

const same = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').trim() === (b ?? '').trim();

/**
 * Takes the driver's copied details off every passenger who is no longer driving.
 *
 * Copying the driver's name onto whoever holds the flag never undid itself, so moving the role left
 * the previous holder still carrying the driver's name — two passengers on one booking as the same
 * person. Only a passenger whose name still matches the driver's is cleared: one whose name was typed
 * in over the copy is somebody else, and keeps it. The phone goes only when it matches too.
 *
 * <b>The primary contact is never cleared.</b> Their name is the name on the booking, and it is
 * pre-filled from the account holder — which is also where the driver details start — so the two
 * often match without either having been copied from the other. Losing the name on the booking to a
 * driver change is worse than a passenger left carrying a name someone has to overtype.
 *
 * Returns whether anything changed.
 */
export function clearCopiedDriverDetails(
	source: PassengerTicketSource | null | undefined,
	details: DriverDetails,
): boolean {
	if (!source) {
		return false;
	}
	// A driver with no name has nothing to have copied, and matching on two blanks would wipe every
	// passenger still waiting to be filled in.
	if (!(details.firstName ?? '').trim() && !(details.lastName ?? '').trim()) {
		return false;
	}

	let changed = false;
	for (const passengerType of passengerTypeStore.allPassengerTypes) {
		const key = passengerType.passengerTypeKey;
		const tickets = ticketsFor(source, key);
		if (!tickets) {
			continue;
		}

		let typeChanged = false;
		for (const ticket of tickets) {
			if (
				!ticket
				|| ticket.driver === true
				|| ticket.isPrimaryContact === true
				|| !same(ticket.firstName, details.firstName)
				|| !same(ticket.lastName, details.lastName)
			) {
				continue;
			}
			ticket.firstName = '';
			ticket.lastName = '';
			if (same(ticket.phone, details.phone)) {
				ticket.phone = '';
			}
			typeChanged = true;
		}

		if (typeChanged) {
			touch(source, key, tickets);
			changed = true;
		}
	}
	return changed;
}
